export const routes = {
  anonLanding: "/",
  newToMedicare: "/new-to-medicare",
  findPlansNow: "/find-plans-now",
  comparePlans: "/compare-plans",
  yearOverYear: "/year-over-year",
  coverageWizard: {
    landingPage: "/coverage-options",
    options: "/coverage-options/options",
    info: "/coverage-options/info",
  },
  inNetworkPharmacy: "/in-network-pharmacy/:id",
  logout: "/logout",
  csrLanding: "/csr",
  csrGuestAccess: "/csr-guest-access",
  drugSearchPreferences: "/drug-search-preferences",
  enroll: "/enroll",
  enrollmentStatus: "/enrollment-status",
  lisQuestions: "/extra-help",
  managePrescriptions: "/manage-prescriptions",
  managePharmacies: "/manage-pharmacies",
  mbpLandingPage: "/mbp-callback",
  pace: {
    landingPage: "/pace",
    plans: "/pace/states/:state",
  },
  pap: {
    landingPage: "/pharmaceutical-assistance-program",
    plans: "/pharmaceutical-assistance-program/drugs/:drug",
  },
  pdePage: "/add-prescriptions",
  pharmacy: "/pharmacy",
  planDetails: "/plan-details/:id",
  prescriptionsList: "/view-prescriptions",
  questionRouting: "/questions",
  sanctionedPlans: "/sanctioned-plans",
  searchResults: "/search-results",
  slsCallback: "/sls-callback",
  spap: {
    landingPage: "/pharmaceutical-assistance-program/states",
    plans: "/pharmaceutical-assistance-program/states/:state",
  },
  summary: {
    drugSearchPreferences: "/summary/drug-search-preferences",
    landingPage: "/summary",
    lisQuestions: "/summary/extra-help",
    managePharmacies: "/summary/manage-pharmacies",
    managePrescriptions: "/summary/manage-prescriptions",
    manageProviders: "/summary/manage-providers",
    pdePage: "/summary/add-prescriptions",
    searchResults: "/summary/search-results",
    selectPharmacies: "/summary/pharmacy",
  },
  version: "/version",
  planPreview: "/plan-preview/:id",
  medigap: {
    landingPage: "/m",
    plans: "/m/plans",
    planDetails: "/m/plan-details/:medigapPlanType",
    policies: "/m/plan-policies/:medigapPlanType",
  },
  providerNetwork: {
    addFacilities: "/facilities",
    addProviders: "/providers",
    manageFacilities: "/manage-facilities",
    manageProviders: "/manage-providers",
  },
  visualRegression: {
    components: "/vr/components",
  },
} as const;

/**
 * Add to this array any routes where the Global/Consistent Header should not
 * be displayed
 */
export const hideGlobalHeaderPaths = [routes.planPreview];

export const beneLandingAndCallbackRoutes: string[] = [
  routes.summary.landingPage,
  routes.slsCallback,
  routes.mbpLandingPage,
];

type HashedRoute<
  T extends string | Record<string, string | Record<string, string>>,
> = {
  [P in keyof T]: T[P] extends string
    ? `./#${T[P]}`
    : T[P] extends Record<string, string>
      ? { [X in keyof T[P]]: `./#${T[P][X]}` }
      : never;
};
type Routes = typeof routes;
type RouteKeys = keyof typeof routes;
type RouteValues = Routes[RouteKeys];
type HashedRoutes = HashedRoute<Routes> & HashedRoute<RouteValues>;

/**
 * Takes a hashed-route path and prepend the leading slash, hash, and relative path
 * symbol (`.`) to create a string that works with the `new URL` constructor when
 * concatenated with a base URL (protocol + path suffixed with a slash)
 */
const createHashedRoute = (route: RouteValues) => `./#${route}`;

/**
 * Takes all of our routes and uses `createHashedRoute` to make test-friendly routes
 * that work with Playwright's `page.goto` method, unprefixed, along with the `baseUrl`
 * set in default configuration
 *
 * E.g., `routes.summary.landingPage` is `/summary` so
 * `hashedRoutes.summary.landingPage` (output of `routeHasher(routes)`) is `./#/summary`
 */
export const hashedRoutes = Object.fromEntries(
  Object.entries(routes).map(([key, value]) => [
    key,
    typeof value === "string"
      ? createHashedRoute(value as RouteValues)
      : Object.fromEntries(
          Object.entries(value).map(([k, v]) => [
            k,
            createHashedRoute(v as RouteValues),
          ])
        ),
  ])
) as HashedRoutes;

/**
 * Add any true landing page routes here. These are places a user could logically
 * land from a link outside MPF, and focus should not be moved to any internal
 * elements on these routes
 */
export const landingPageRoutes = [
  routes.anonLanding,
  routes.summary.landingPage,
  routes.csrLanding,
  routes.medigap.landingPage,
  routes.pace.landingPage,
  routes.pap.landingPage,
  routes.spap.landingPage,
  routes.coverageWizard.landingPage,
];

export function getInpfRoute(id: string) {
  return routes.inNetworkPharmacy.replace(":id", id || "");
}
